import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';
import type { UseFormRegisterReturn } from 'react-hook-form';

import { Title, SidepanelTitle as UiSidepanelTitle } from '~/ui/domain/Sidepanel/components/SidepanelTitle';

import { useTitleThreadState } from '../../comments/CommentableTitle';
import { useTitleCommentRow } from '../../comments/title-comment-row-context';
import { JobProfileStatusStamps } from '../job-profile-section/JobProfileStatusStamps';

export { Title };

type SidepanelTitleProps = {
    readonly architectureId?: string;
    readonly className?: string;
    readonly hasComment?: boolean;
    readonly isCommentActive?: boolean;
    readonly isEditing: boolean;
    readonly itemId?: string;
    readonly itemType?: 'job-level' | 'job-role';
    readonly onInput?: () => void;
    readonly placeholder?: string;
    readonly register: UseFormRegisterReturn<`translations.${number}.title`>;
    readonly title: string;
    readonly version?: string;
};

export const SidepanelTitle: FunctionComponent<SidepanelTitleProps> = ({
    architectureId,
    className = 'mt-12xl',
    hasComment,
    isCommentActive,
    isEditing,
    itemId,
    itemType,
    onInput,
    placeholder,
    register,
    title,
    version,
}) => {
    const titleThreadState = useTitleThreadState();
    const { rowDataAttrs, onRowClick, titleInputRef } = useTitleCommentRow();
    const effectiveIsCommentActive = isCommentActive ?? titleThreadState.isActive;
    const effectiveHasComment = hasComment ?? titleThreadState.hasComment;

    const versionNumber = is.nonEmptyStringAndNotWhitespace(version) ? Number(version) : Number.NaN;
    const canRenderStamps =
        !is.nullOrUndefined(architectureId) &&
        !is.nullOrUndefined(itemId) &&
        !is.nullOrUndefined(itemType) &&
        Number.isFinite(versionNumber);

    const stamps = canRenderStamps ? (
        <JobProfileStatusStamps
            architectureId={architectureId}
            currentVersion={versionNumber}
            itemId={itemId}
            itemType={itemType}
        />
    ) : undefined;

    return (
        <UiSidepanelTitle
            className={className}
            commentHighlightClassName={titleThreadState.highlightClassName}
            hasComment={effectiveHasComment}
            inputRef={titleInputRef}
            isCommentActive={effectiveIsCommentActive}
            isEditing={isEditing}
            onInput={onInput}
            onRowClick={onRowClick}
            placeholder={placeholder}
            register={register}
            rowAttrs={rowDataAttrs}
            stamps={stamps}
            title={title}
        />
    );
};
